import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllFollowables,
  getMostFollowed,
  getTrendingFollowers,
  getLiveNow,
  followEntityTypeLabels,
  followEntityTypeIcons,
  followEntityTypeColors,
  followEntityTypeBgLight,
  followEntityTypeTextColor,
  followNotifications,
  followScoreActions,
  followerDashboardStats,
  listenerProfile,
  trendConfig,
  formatFollowerCount,
  formatGrowthPercent,
  type FollowableEntity,
  type FollowEntityType,
} from '@/mocks/rfwFollowers';

function FollowCard({ entity, variant = 'default' }: { entity: FollowableEntity; variant?: 'default' | 'compact' | 'trending' }) {
  const [isFollowing, setIsFollowing] = useState(() => {
    try {
      const saved = localStorage.getItem(`follow_${entity.id}`);
      return saved === 'true';
    } catch { return false; }
  });
  const [followCount, setFollowCount] = useState(entity.followers);

  const toggleFollow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newState = !isFollowing;
    setIsFollowing(newState);
    setFollowCount((prev) => newState ? prev + 1 : prev - 1);
    localStorage.setItem(`follow_${entity.id}`, String(newState));
  };

  if (variant === 'compact') {
    return (
      <Link
        to={entity.linkUrl}
        className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 group"
      >
        <img src={entity.imageUrl} alt={entity.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${followEntityTypeColors[entity.type]}`}>
              {followEntityTypeLabels[entity.type]}
            </span>
            {entity.isLive && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                {entity.liveLabel}
              </span>
            )}
          </div>
          <p className="text-xs font-semibold text-foreground-900 leading-snug line-clamp-1">{entity.name}</p>
          <p className="text-[10px] text-foreground-500">{formatFollowerCount(entity.followers)} seguidores</p>
        </div>
      </Link>
    );
  }

  if (variant === 'trending') {
    return (
      <Link
        to={entity.linkUrl}
        className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 group"
      >
        <div className="relative shrink-0">
          <img src={entity.imageUrl} alt={entity.name} className="w-12 h-12 rounded-full object-cover" />
          {entity.isLive && (
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-background-50 flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${followEntityTypeColors[entity.type]}`}>
              {followEntityTypeLabels[entity.type]}
            </span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${trendConfig[entity.trend].bgColor} ${trendConfig[entity.trend].color}`}>
              <i className={`${trendConfig[entity.trend].icon} text-[8px]`}></i>
              {formatGrowthPercent(entity.trendPercent)}
            </span>
          </div>
          <p className="text-sm font-bold text-foreground-900 leading-snug mb-0.5">{entity.name}</p>
          <p className="text-[11px] text-foreground-500">{entity.subtitle}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-extrabold text-foreground-900">{formatFollowerCount(followCount)}</p>
          <p className="text-[10px] text-foreground-500">seguidores</p>
        </div>
      </Link>
    );
  }

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group">
      <div className="relative">
        <img src={entity.imageUrl} alt={entity.name} className="w-full h-40 object-cover object-top" />
        {entity.isLive && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            {entity.liveLabel}
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        <span className={`absolute bottom-3 left-3 px-2 py-0.5 rounded-full text-[9px] font-medium border border-white/30 text-white bg-black/30 backdrop-blur-sm`}>
          {followEntityTypeLabels[entity.type]}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-foreground-900 leading-snug mb-0.5 line-clamp-1">{entity.name}</p>
            <p className="text-[11px] text-foreground-500 line-clamp-1">{entity.subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          {entity.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">{tag}</span>
          ))}
          {entity.tags.length > 2 && (
            <span className="text-[9px] text-foreground-400">+{entity.tags.length - 2}</span>
          )}
        </div>

        <div className="flex items-center justify-between text-[11px] mb-3">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-foreground-500">
              <i className="ri-group-line text-[10px]"></i>
              {formatFollowerCount(followCount)}
            </span>
            <span className={`flex items-center gap-1 ${trendConfig[entity.trend].color}`}>
              <i className={`${trendConfig[entity.trend].icon} text-[10px]`}></i>
              {formatGrowthPercent(entity.trendPercent)}
            </span>
          </div>
          <span className="flex items-center gap-1 text-primary-600 font-semibold">
            <i className="ri-flashlight-line text-[10px]"></i>+{entity.scoreReward} pts
          </span>
        </div>

        <button
          onClick={toggleFollow}
          className={`w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
            isFollowing
              ? 'bg-primary-100 text-primary-700 border border-primary-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
              : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        >
          <i className={`${isFollowing ? 'ri-user-unfollow-line' : 'ri-user-add-line'} text-[11px]`}></i>
          {isFollowing ? 'Siguiendo' : 'Seguir'}
        </button>
      </div>
    </div>
  );
}

function LiveStripCard({ entity }: { entity: FollowableEntity }) {
  return (
    <Link
      to={entity.linkUrl}
      className="flex-shrink-0 w-48 rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-red-300/60 transition-all duration-200 group"
    >
      <div className="relative h-24">
        <img src={entity.imageUrl} alt={entity.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/90 text-white text-[8px] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          {entity.liveLabel}
        </span>
        <p className="absolute bottom-2 left-2 right-2 text-[10px] font-bold text-white leading-snug line-clamp-2">{entity.name}</p>
      </div>
      <div className="p-2 flex items-center justify-between">
        <span className="text-[9px] text-foreground-500">{formatFollowerCount(entity.followers)} seguidores</span>
        <span className="text-[9px] font-semibold text-primary-600">+{entity.scoreReward} pts</span>
      </div>
    </Link>
  );
}

export default function SeguidoresSection() {
  const [activeTab, setActiveTab] = useState<'mostFollowed' | 'trending'>('mostFollowed');

  const mostFollowed = getMostFollowed(6);
  const trending = getTrendingFollowers(6);
  const liveNow = getLiveNow().slice(0, 6);

  const tabs = [
    { id: 'mostFollowed' as const, label: 'Más Seguidos', icon: 'ri-group-line', count: mostFollowed.length },
    { id: 'trending' as const, label: 'Tendencia de Seguidores', icon: 'ri-line-chart-line', count: trending.length },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            SISTEMA DE SEGUIDORES RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Todo Puede <span className="text-primary-600">Seguirse</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Radios, Programas, Locutores, Clubes, Eventos y Concursos. Seguí lo que te importa y recibí notificaciones cuando algo pase.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Total Follows', value: formatFollowerCount(followerDashboardStats.totalFollows), icon: 'ri-group-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Esta Semana', value: formatFollowerCount(followerDashboardStats.weeklyFollows), icon: 'ri-arrow-up-circle-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Siguiendo Promedio', value: followerDashboardStats.avgFollowsPerListener.toString(), icon: 'ri-user-heart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Retención', value: `${followerDashboardStats.retentionRate}%`, icon: 'ri-refresh-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Oyentes Activos', value: formatFollowerCount(followerDashboardStats.activeListenersFollowing), icon: 'ri-user-star-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Live Now Strip */}
        {liveNow.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              </div>
              <p className="text-sm font-bold text-foreground-900">Siguiendo — En Vivo Ahora</p>
              <span className="text-[10px] text-foreground-500">{liveNow.length} activos</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {liveNow.map((entity) => (
                <LiveStripCard key={entity.id} entity={entity} />
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm cursor-pointer'
                    : 'text-foreground-600 hover:text-foreground-900 cursor-pointer'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(activeTab === 'mostFollowed' ? mostFollowed : trending).map((entity) => (
            <FollowCard key={entity.id} entity={entity} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/seguidores"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-group-line"></i>
            Explorar Todos los Seguidores
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-user-heart-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Los seguidores son uno de los activos más importantes de RFW.
          </p>
          <p className="text-xs text-foreground-600">
            Cada seguidor es una persona real que eligió estar conectada con vos. Cuidalos, interactuá con ellos y hacelos crecer.
          </p>
        </div>
      </div>
    </section>
  );
}